import alt from "../alt";
import indexBy from "lodash/keyBy";
import extend from "lodash/assign";
import moment from "moment";
import UserActions from "../actions/UserActions";
import UserUtil from "../utils/UserUtil";
import EventBucketActions from "../actions/EventBucketActions";
import SearchActions from "../actions/SearchActions";
import OrderActions from "../actions/OrderActions";
import ShiftActions from "../actions/ShiftActions";
import SystemStatesActions from "../actions/SystemStatesActions";
import logger from "../logger";
import { history } from "../util";

const SHOULD_PERSIST = !!window.localStorage;
const LS_KEY = "eventive-userState";
let cachedUser =
  (SHOULD_PERSIST &&
    window.localStorage[LS_KEY] &&
    JSON.parse(window.localStorage[LS_KEY])) ||
  {};

// Restore user context from cache if specificed
if (cachedUser.data) {
  logger.setUserContext({
    id: cachedUser.data.id,
    email: cachedUser.data.email
  });
}

// Inject god user from url if necessary
const activeGodQuery = /_god_become_user=([^&]+)/g;
const result = activeGodQuery.exec(window.location.search);
if (result) {
  cachedUser.activeGodUser = result[1];
}

class UserStore {
  constructor() {
    this.bindActions(UserActions);

    this.state = extend(
      {
        // SELF
        isLoggedIn: false,
        apiKey: null,
        loginSuccess: false,
        loginInProgress: false,
        loginErrorMessage: null,
        data: null,

        forgotSuccess: false,
        forgotInProgress: false,
        forgotErrorMessage: null,

        resetSuccess: false,
        resetInProgress: false,
        resetErrorMessage: null,

        uploadedFiles: {},

        contentCreatorInvites: {},
        contentCreatorInviteLookupErrorMessage: null,
        acceptInProgress: false,

        revenueSharingInvites: {},
        revenueSharingInviteLookupErrorMessage: null,

        // OTHERS/ORG
        user: null,
        accounts: {},
        roles: {},

        // GODMODE
        allUsers: {},

        condensedUsers: [],
        activeGodUser: null,
        activeGodRole: null,
        godStealthMode: false,

        //paginated user page
        paginatedUsers: {
          page: -1,
          sorted: [{ id: "organization", desc: false }],
          pages: 0,
          users: [],
          searchTerm: ""
        },
        paginatedContentCreators: {
          page: -1,
          sorted: [{ id: "organization", desc: false }],
          pages: 0,
          users: [],
          searchTerm: ""
        },
        loadingUsers: false,
        loadingContentCreators: false
      },
      {
        isLoggedIn: cachedUser.isLoggedIn,
        apiKey: cachedUser.apiKey,
        data: cachedUser.data,
        activeGodUser: cachedUser.activeGodUser
      }
    );

    // If logged in, fire off initial system state fetch immediately
    if (this.state.isLoggedIn) {
      SystemStatesActions.fetchLocationData.defer();
    }

    this.exportPublicMethods({
      isLoggedIn: this.isLoggedIn.bind(this),
      hasRole: this.hasRole.bind(this),
      hasPermission: this.hasPermission.bind(this),
      isGod: this.isGod.bind(this),
      isBillingGod: this.isBillingGod.bind(this),
      isGodInAnyForm: this.isGodInAnyForm.bind(this),
      isEnterprise: this.isEnterprise.bind(this),
      getGodCredentials: this.getGodCredentials.bind(this),
      getCredentials: this.getCredentials.bind(this),
      makeApiKeyQueryString: this.makeApiKeyQueryString.bind(this),
      getUserId: this.getUserId.bind(this),
      getUser: this.getUser.bind(this),
      getUserType: this.getUserType.bind(this),
      getAccount: this.getAccount.bind(this)
    });
  }

  // PUBLIC METHODS
  // ==============

  isLoggedIn() {
    return this.state.isLoggedIn;
  }

  hasRole(requiredRole) {
    const userRole =
      this.state.activeGodRole || (this.state.data && this.state.data.role);

    // if multiple possible roles are passed, check if the user has one of them
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(userRole);
    }

    return userRole === requiredRole;
  }

  hasPermission(permission) {
    let { data } = this.state;

    if (this.state.activeGodRole) {
      data = this.state.roles[this.state.activeGodRole];
    }

    if (!data || !data.permissions) {
      return false;
    }
    if (permission === "god") {
      return this.isGod();
    }
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return (
      data.permissions === "*" ||
      permission.some(p => data.permissions.indexOf(p) !== -1)
    );
  }

  isGodInAnyForm() {
    let { data } = this.state;
    return data && data.god ? true : false;
  }

  isGod() {
    let { data, godStealthMode } = this.state;
    return data && data.god && !godStealthMode ? true : false;
  }

  isBillingGod() {
    let { data, godStealthMode } = this.state;
    return data && data.billing_god && !godStealthMode ? true : false;
  }

  isEnterprise() {
    let { data, godStealthMode } = this.state;
    if (this.state.activeGodUser && this.state.allUsers[this.state.activeGodUser]) {
      return this.state.allUsers[this.state.activeGodUser].is_enterprise;
    }
    else {
      return data && data.owner && data.owner.is_enterprise;
    }
  }

  getGodCredentials() {
    return (
      this.isGodInAnyForm() && {
        apiKey: this.state.apiKey,
        activeUser: this.state.activeGodUser
      }
    );
  }

  getCredentials() {
    if (this.isGodInAnyForm()) {
      let { apiKey, activeUser } = this.getGodCredentials();
      return { api_key: apiKey, god_become_user: activeUser };
    } else {
      return { api_key: this.state.apiKey };
    }
  }

  makeApiKeyQueryString() {
    if (this.isGodInAnyForm()) {
      let { apiKey, activeUser } = this.getGodCredentials();
      return `api_key=${apiKey}&god_become_user=${activeUser}`;
    } else {
      return `api_key=${this.state.apiKey}`;
    }
  }

  getUserId() {
    if (this.isGodInAnyForm()) {
      return this.state.activeGodUser;
    } else {
      return (
        this.state.data && this.state.data.owner && this.state.data.owner.id
      );
    }
  }

  getUser() {
    if (this.isGodInAnyForm()) {
      return this.state.allUsers[this.state.activeGodUser];
    } else {
      return this.state.data && this.state.data.owner;
    }
  }

  getAccount() {
    return this.state.data;
  }

  getUserType() {
    const user = this.getUser();
    if (user) {
      return user.type;
    } else {
      return null;
    }
  }

  // SELF-MANIPULATION (logging in/editing/etc)
  // ==========================================

  onUpdateApiKey(apiKey) {
    this.setState({ apiKey });
  }

  onLogin(credentials) {
    this.setState({ loginInProgress: true, loginErrorMessage: null });
    UserUtil.login(
      credentials.email,
      credentials.password,
      credentials.noRedirect
    )
      .then(data => {
        UserActions.updateApiKey(data.token);
        UserActions.loginSucceeded(data);
      })
      .catch(UserActions.loginFailed);
  }

  onLoginWithToken(token) {
    this.setState({ apiKey: token });
    UserUtil.fetchOwnAccount(this.state.apiKey)
      .then(data => {
        UserActions.loginSucceeded(data);
      })
      .catch(UserActions.fetchFailed);
  }

  onLoginSucceeded(data) {
    this.setState({
      isLoggedIn: true,
      loginSuccess: true,
      loginInProgress: false,
      loginErrorMessage: null,
      data: data
    });

    logger.setUserContext({ id: data.id, email: data.email });

    window.Intercom("boot", {
      app_id: "oj5vkp3b",
      email: data.email,
      user_id: data.id
    });

    if (data.god) {
      UserActions.fetchAllUsers.defer();
      UserActions.fetchCondensedUsers.defer();
    } else {
      EventBucketActions.fetchAll.defer();
    }

    // Reset when new user logged in
    ShiftActions.reset.defer();
    OrderActions.reset.defer();
    SearchActions.reset.defer();
    SystemStatesActions.fetchLocationData.defer();
  }

  onLoginFailed(e) {
    this.setState({
      isLoggedIn: false,
      loginInProgress: false,
      loginSuccess: false,
      loginErrorMessage: e.message
    });

    window.Intercom("trackEvent", "login-failed");
  }

  onClearErrors() {
    this.setState({
      loginInProgress: false,
      loginErrorMessage: null
    });
  }

  onLogout(noRedirect) {
    this.setState({
      isLoggedIn: false,
      apiKey: null,
      data: null,
      user: null,
      accounts: {},
      loginSuccess: false,
      loginInProgress: false,
      loginErrorMessage: null
    });

    logger.setUserContext();
    window.Intercom("shutdown");
    if (!noRedirect) {
      history.push("/login");
    }
  }

  onFetch() {
    UserUtil.fetchOwnAccount(this.state.apiKey)
      .then(data => {
        UserActions.setData(data);
      })
      .catch(UserActions.fetchFailed);
  }

  onFetchUser() {
    UserUtil.fetchUser(this.state.apiKey)
      .then(user => {
        this.setState({ user });
      })
      .catch(UserActions.fetchFailed);
  }

  onEditUser(data) {
    UserUtil.editUser(this.state.apiKey, "self", data)
      .then(user => {
        this.setState({ user });
        UserActions.fetch(); // fetch self to refresh status (e.g. needs_stripe)
      })
      .catch(UserActions.editFailed);
  }

  onFetchRoles() {
    UserUtil.fetchRoles(this.state.apiKey)
      .then(roles => {
        let finalRoles = {};
        Object.keys(roles).forEach(k => {
          if (
            !roles[k].allowed_user_types ||
            roles[k].allowed_user_types.indexOf(this.getUserType()) !== -1
          ) {
            finalRoles[k] = roles[k];
          }
        });
        this.setState({ roles: finalRoles });
      })
      .catch(UserActions.fetchFailed);
  }

  onEdit(data) {
    UserUtil.editAccount(this.state.apiKey, "self", data)
      .then(updated => {
        UserActions.setData(updated);
      })
      .catch(UserActions.editFailed);
  }

  onSetData(data) {
    this.setState({
      data: data
    });
    logger.setUserContext({ id: data.id, email: data.email });
  }

  onForgotPassword(email) {
    this.setState({
      forgotInProgress: true
    });
    UserUtil.forgotPassword(email)
      .then(UserActions.forgotSucceeded)
      .catch(UserActions.forgotFailed);
  }

  onForgotSucceeded() {
    this.setState({
      forgotInProgress: false,
      forgotSuccess: true,
      forgotErrorMessage: null
    });
  }

  onForgotFailed(e) {
    this.setState({
      forgotInProgress: false,
      forgotSuccess: false,
      forgotErrorMessage: e.message
    });
  }

  onResetPassword(data) {
    this.setState({
      resetInProgress: true
    });
    UserUtil.resetPassword(data.token, data.password)
      .then(UserActions.resetSucceeded)
      .catch(UserActions.resetFailed);
  }

  onResetSucceeded() {
    this.setState({
      resetInProgress: false,
      resetSuccess: true,
      resetErrorMessage: null
    });
  }

  onResetFailed(e) {
    this.setState({
      resetInProgress: false,
      resetSuccess: false,
      resetErrorMessage: e.message
    });
  }

  onUserSignup(options) {
    this.setState({ loginInProgress: true, loginErrorMessage: null });
    UserUtil.signup(options)
      .then(data => {
        UserActions.updateApiKey(data.token);
        UserActions.loginSucceeded(data);
      })
      .catch(UserActions.loginFailed);
  }

  onLookupContentCreatorInvite(token) {
    UserUtil.lookupContentCreatorInvite(this.state.apiKey, token)
      .then(data => {
        this.setState({
          contentCreatorInvites: {
            ...this.state.contentCreatorInvites,
            [token]: data
          }
        });
      })
      .catch(e => {
        console.log(e);
        this.setState({
          contentCreatorInviteLookupErrorMessage:
            (e && e.message) || "Unable to find invite"
        });
      });
  }

  onAcceptContentCreatorInvite(token) {
    this.setState({ acceptInProgress: true });
    UserUtil.acceptContentCreatorInvite(this.state.apiKey, token)
      .then(data => {
        this.setState({ acceptInProgress: false });
        if (data.title && data.title_delivery) {
          history.push(
            `titles/${data.title}/deliveries/${data.title_delivery}`
          );
        } else if (data.film) {
          history.push(`creator_hub/films/${data.film}`);
        }
      })
      .catch(UserActions.editFailed);
  }

  onLookupRevenueSharingInvite(token) {
    UserUtil.lookupRevenueSharingInvite(token)
      .then(data => {
        this.setState({
          revenueSharingInvites: {
            ...this.state.revenueSharingInvites,
            [token]: data
          }
        });
      })
      .catch(e => {
        console.log(e);
        this.setState({
          revenueSharingInviteLookupErrorMessage:
            (e && e.message) || "Unable to find invite"
        });
      });
  }

  onAcceptRevenueSharingInvite(token) {
    UserUtil.acceptRevenueSharingInvite(this.state.apiKey, token)
      .then(data => {
        this.setState({
          revenueSharingInvites: {
            ...this.state.revenueSharingInvites,
            [token]: {
              ...this.state.revenueSharingInvites[token],
              accepted: true
            }
          }
        });
      })
      .catch(UserActions.editFailed);
  }

  // MANIPULATION OF OTHERS
  // ======================

  onFetchAccounts() {
    UserUtil.fetchAccounts(this.state.apiKey)
      .then(data => {
        this.setState({ accounts: indexBy(data.accounts, "id") });
      })
      .catch(UserActions.fetchFailed);
  }

  onDeleteAccount(id) {
    UserUtil.deleteAccount(this.state.apiKey, id)
      .then(() => {
        let accounts = Object.assign({}, this.state.accounts);
        delete accounts[id];
        this.setState({ accounts });
      })
      .catch(UserActions.fetchFailed);
  }

  onCreateAccount(values) {
    UserUtil.createAccount(this.state.apiKey, values)
      .then(data => {
        let { accounts } = this.state;
        accounts[data.id] = data;
        this.setState({ accounts });
      })
      .catch(UserActions.editFailed);
  }

  onEditAccount({ id, values }) {
    UserUtil.editAccount(this.state.apiKey, id, values)
      .then(data => {
        let { accounts } = this.state;
        accounts[id] = data;
        this.setState({ accounts });
      })
      .catch(UserActions.editFailed);
  }

  onUploadFile({ key, file }) {
    UserUtil.uploadFile(UserStore.getState().apiKey, key, file)
      .then(data => {
        let { uploadedFiles } = this.state;
        uploadedFiles[key] = data.url;
        this.setState({ uploadedFiles });
      })
      .catch(UserActions.editFailed);
  }

  // GODMODE
  // =======

  onFetchAllUsers() {
    if (this.state.activeGodUser) {
      UserUtil.fetchUser(this.state.apiKey, {
        apiKey: this.state.apiKey,
        activeUser: this.state.activeGodUser
      })
        .then(data => {
          this.setState({
            allUsers: { [this.state.activeGodUser]: data }
          });
          EventBucketActions.fetchAll.defer();
        })
        .catch(UserActions.fetchFailed);
    }
    UserUtil.fetchAllUsers(this.state.apiKey, { excludeContentCreators: true })
      .then(data => {
        this.setState({
          allUsers: Object.assign(
            {},
            this.state.allUsers,
            indexBy(data.users, "id")
          ),
          activeGodUser: this.state.activeGodUser || data.users[0].id
        });
        EventBucketActions.fetchAll.defer();
      })
      .catch(UserActions.fetchFailed);
    UserUtil.fetchAllUsers(this.state.apiKey, { onlyContentCreators: true })
      .then(data => {
        this.setState({
          allUsers: Object.assign(
            {},
            this.state.allUsers,
            indexBy(data.users, "id")
          )
        });
      })
      .catch(UserActions.fetchFailed);
  }

  onFetchPaginatedUsers({ page, isContentCreators, sorted, searchTerm }) {
    this.setState({
      [isContentCreators ? "loadingContentCreators" : "loadingUsers"]: true
    });
    UserUtil.fetchPaginatedUsers(
      this.state.apiKey,
      page,
      isContentCreators,
      sorted,
      searchTerm
    ).then(data => {
      if (isContentCreators) {
        this.setState({
          paginatedContentCreators: { ...data, page, sorted, searchTerm },
          loadingContentCreators: false
        });
      } else {
        this.setState({
          paginatedUsers: { ...data, page, sorted, searchTerm },
          loadingUsers: false
        });
      }
    });
  }

  onFetchCurrentPage(isContentCreators) {
    this.setState({
      [isContentCreators ? "loadingContentCreators" : "loadingUsers"]: true
    });
    const dataSet = isContentCreators
      ? this.state.paginatedUsers
      : this.state.paginatedContentCreators;
    UserUtil.fetchPaginatedUsers(
      this.state.apiKey,
      dataSet.page,
      isContentCreators,
      dataSet.sorted
    ).then(data => {
      if (isContentCreators) {
        this.setState({
          paginatedContentCreators: {
            ...data,
            page: dataSet.page,
            sorted: dataSet.sorted
          },
          loadingContentCreators: false
        });
      } else {
        this.setState({
          paginatedUsers: {
            ...data,
            page: dataSet.page,
            sorted: dataSet.sorted
          },
          loadingUsers: false
        });
      }
    });
  }

  onFetchCondensedUsers() {
    UserUtil.fetchCondensedUsers(this.state.apiKey)
      .then(data => {
        this.setState({
          condensedUsers: data
        });
      })
      .catch(UserActions.fetchFailed);
  }

  onSetGodUser(activeGodUser) {
    this.setState({ activeGodUser });

    if (!this.state.allUsers[activeGodUser]) {
      UserUtil.fetchUser(this.state.apiKey, {
        apiKey: this.state.apiKey,
        activeUser: this.state.activeGodUser
      })
        .then(data => {
          this.setState({
            allUsers: {
              ...this.state.allUsers,
              [this.state.activeGodUser]: data
            }
          });
        })
        .catch(UserActions.fetchFailed);
    }

    EventBucketActions.updateEventBuckets.defer({});
    EventBucketActions.fetchAll.defer();
    OrderActions.reset.defer();
    SearchActions.reset.defer();
  }

  onSetGodRole(activeGodRole) {
    this.setState({ activeGodRole });
  }

  onGodCreateUser(values) {
    UserUtil.createUser(this.state.apiKey, values)
      .then(data => {
        this.setState({
          allUsers: Object.assign({}, this.state.allUsers, { [data.id]: data }),
          condensedUsers: [
            ...this.state.condensedUsers,
            { id: data.id, organization: data.organization, type: data.type }
          ]
        });
        UserActions.fetchCurrentPage(false);
      })
      .catch(UserActions.editFailed);
  }

  onGodAutoCreateUser(user) {
    this.setState({
      allUsers: Object.assign({}, this.state.allUsers, {
        [user.id]: user
      }),
      condensedUsers: [
        ...this.state.condensedUsers,
        { id: user.id, organization: user.organization, type: user.type }
      ]
    });
  }

  onGodEditUser({ id, values }) {
    UserUtil.editUser(this.state.apiKey, id, values)
      .then(data => {
        const newCondensedUser = [...this.state.condensedUsers];
        const condensedUserIndex = newCondensedUser.findIndex(
          user => user.id === data.id
        );
        newCondensedUser[condensedUserIndex] = data;

        this.setState({
          allUsers: Object.assign({}, this.state.allUsers, { [data.id]: data }),
          condensedUsers: newCondensedUser
        });
        UserActions.fetchCurrentPage(data.type === "content_creator");
      })
      .catch(UserActions.editFailed);
  }

  onGodDeleteUser(id) {
    UserUtil.deleteUser(this.state.apiKey, id)
      .then(() => {
        let allUsers = Object.assign({}, this.state.allUsers);
        UserActions.fetchCurrentPage(allUsers[id].type === "content_creator");
        delete allUsers[id];
        this.setState({
          allUsers,
          condensedUsers: this.state.condensedUsers.filter(
            user => user.id !== id
          )
        });
      })
      .catch(UserActions.fetchFailed);
  }

  onGodSetStealthMode(godStealthMode) {
    this.setState({
      godStealthMode,
      activeGodRole: godStealthMode ? this.state.activeGodRole : null
    });
  }

  // MISC
  // ====

  onFetchFailed(e) {
    alert(`Fetch failed: ${e.message}`);
    throw e;
  }

  onEditFailed(e) {
    alert(`Edit failed: ${e.message}`);
    //throw e; this is normal
  }
}

let store = alt.createStore(UserStore, "UserStore");

if (SHOULD_PERSIST) {
  store.listen(state => {
    // Currently disabled: Update date-formatting locale
    // if (store.getUser() && store.getUser().locale) {
    //   moment.locale(store.getUser().locale);
    // }

    window.localStorage[LS_KEY] = JSON.stringify({
      isLoggedIn: state.isLoggedIn,
      apiKey: state.apiKey,
      data: state.data,
      activeGodUser: state.activeGodUser
    });
  });
}

// if still logged in using old auth method, force a logout
if (cachedUser.data && !cachedUser.data.owner) {
  UserActions.logout();
}
if (cachedUser.data && !cachedUser.data.hash) {
  UserActions.logout();
}

// Populate initial user data on initial load
if (cachedUser.apiKey) {
  UserActions.fetch();
  if (cachedUser.data.god) {
    UserActions.fetchAllUsers();
    UserActions.fetchCondensedUsers();
  }
}

export default store;



// WEBPACK FOOTER //
// ./src/app/stores/UserStore.js